import { Component, computed, inject, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { normalizeError } from './core/errors';
import { Asset, PlaybackResponse } from './core/models';
import { VdocipherApiService } from './core/vdocipher-api.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './app.component.html',
})
export class AppComponent {
  private readonly vdocipherApi = inject(VdocipherApiService);
  private readonly sanitizer = inject(DomSanitizer);

  readonly assets = signal<Asset[]>([]);
  readonly selectedAsset = signal<Asset | null>(null);
  readonly playback = signal<PlaybackResponse | null>(null);
  readonly playbackUrl = signal<SafeResourceUrl | null>(null);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly apiBaseUrl = this.vdocipherApi.apiBaseUrl;

  newTitle = '';
  newSourceUrl = '';
  newFolderId = 'root';
  watermarkText = '';
  appliedWatermarkText = '';
  selectedUploadFile: File | null = null;
  readonly uploadProgress = signal<number | null>(null);

  readonly helperText = computed(() => {
    const asset = this.selectedAsset();
    if (!asset) return 'Select an asset from the left panel.';
    if (!asset.ready) return 'vdoCipher is still importing, encrypting, or processing this video. Use Sync after it is ready.';
    return 'A fresh OTP and playbackInfo pair will be requested from Rails.';
  });

  readonly placeholderTitle = computed(() => {
    if (this.busy()) return 'Preparing request';
    if (this.selectedAsset()?.ready) return 'Ready to request playback OTP';
    return 'Waiting for vdoCipher ready status';
  });

  readonly statusMessage = computed(() => {
    if (this.error()) return 'Action failed';
    if (this.busy()) return 'Waiting for backend response';
    const asset = this.selectedAsset();
    if (!asset) return 'Select or create a protected asset';
    if (asset.lastErrorMessage) return 'Asset has a stored error';
    if (!asset.ready) return 'vdoCipher is preparing this DRM asset';
    if (!this.playback()) return 'Asset is ready; request a fresh OTP';
    return 'Session OTP is active for this browser playback';
  });

  constructor() {
    void this.refresh();
  }

  async refresh(): Promise<void> {
    this.busy.set(true);
    this.error.set('');
    try {
      const assets = await this.vdocipherApi.listAssets();
      this.assets.set(assets);
      const selected = this.selectedAsset();
      if (selected) {
        this.selectedAsset.set(assets.find(asset => asset.id === selected.id) ?? null);
      }
    } catch (error) {
      this.error.set(normalizeError(error));
    } finally {
      this.busy.set(false);
    }
  }

  async importAsset(): Promise<void> {
    this.busy.set(true);
    this.error.set('');
    try {
      const asset = await this.vdocipherApi.importUrl({
        title: this.newTitle,
        sourceUrl: this.newSourceUrl,
        folderId: this.newFolderId || 'root',
      });
      this.assets.update(assets => [asset, ...assets]);
      void this.selectAsset(asset);
      this.newTitle = '';
      this.newSourceUrl = '';
    } catch (error) {
      this.error.set(normalizeError(error));
    } finally {
      this.busy.set(false);
    }
  }

  onUploadFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedUploadFile = input.files?.[0] ?? null;
    this.uploadProgress.set(null);
  }

  async uploadAsset(): Promise<void> {
    if (!this.selectedUploadFile) return;

    this.busy.set(true);
    this.error.set('');
    this.uploadProgress.set(0);
    try {
      const result = await this.vdocipherApi.uploadCredentials({
        title: this.newTitle,
        folderId: this.newFolderId || 'root',
      });
      this.assets.update(assets => [result.asset, ...assets]);
      await this.vdocipherApi.uploadFile(result.upload, this.selectedUploadFile, progress => {
        this.uploadProgress.set(progress);
      });
      const completed = await this.vdocipherApi.uploadComplete(result.asset.id);
      this.assets.update(assets => assets.map(asset => asset.id === completed.id ? completed : asset));
      void this.selectAsset(completed);
      this.newTitle = '';
      this.newSourceUrl = '';
      this.selectedUploadFile = null;
      this.uploadProgress.set(null);
    } catch (error) {
      this.error.set(normalizeError(error));
    } finally {
      this.busy.set(false);
    }
  }

  async selectAsset(asset: Asset): Promise<void> {
    this.selectedAsset.set(asset);
    this.playback.set(null);
    this.playbackUrl.set(null);
    if (!asset.ready) return;

    this.busy.set(true);
    this.error.set('');
    try {
      await this.requestPlayback(asset);
    } catch (error) {
      this.error.set(normalizeError(error));
    } finally {
      this.busy.set(false);
    }
  }

  async requestSelectedPlayback(): Promise<void> {
    const asset = this.selectedAsset();
    if (!asset || !asset.ready) return;

    this.busy.set(true);
    this.error.set('');
    try {
      await this.requestPlayback(asset);
    } catch (error) {
      this.error.set(normalizeError(error));
    } finally {
      this.busy.set(false);
    }
  }

  async syncSelectedAsset(): Promise<void> {
    const asset = this.selectedAsset();
    if (!asset) return;

    this.busy.set(true);
    this.error.set('');
    try {
      const synced = await this.vdocipherApi.syncAsset(asset.id);
      this.assets.update(assets => assets.map(item => item.id === synced.id ? synced : item));
      await this.selectAsset(synced);
    } catch (error) {
      this.error.set(normalizeError(error));
    } finally {
      this.busy.set(false);
    }
  }

  async deleteSelectedAsset(): Promise<void> {
    const asset = this.selectedAsset();
    if (!asset) return;

    this.busy.set(true);
    this.error.set('');
    try {
      await this.vdocipherApi.deleteAsset(asset.id);
      this.assets.update(assets => assets.filter(item => item.id !== asset.id));
      this.selectedAsset.set(null);
      this.playback.set(null);
      this.playbackUrl.set(null);
      this.appliedWatermarkText = '';
    } catch (error) {
      this.error.set(normalizeError(error));
      await this.refresh();
    } finally {
      this.busy.set(false);
    }
  }

  async applyWatermark(): Promise<void> {
    await this.requestSelectedPlayback();
  }

  async clearWatermark(): Promise<void> {
    this.watermarkText = '';
    await this.requestSelectedPlayback();
  }

  private async requestPlayback(asset: Asset): Promise<void> {
    const text = this.watermarkText.trim();
    this.playback.set(null);
    this.playbackUrl.set(null);
    this.appliedWatermarkText = '';
    const playback = await this.vdocipherApi.getPlayback(asset.id, {
      watermark: text ? { text } : undefined,
    });
    this.playback.set(playback);
    this.appliedWatermarkText = playback.watermark.enabled ? playback.watermark.text ?? '' : '';
    this.playbackUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(playback.embedUrl));
  }
}
